import * as React from "react";
import { Box, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import {
  BufferGeometry,
  QuadraticBezierCurve3,
  Vector3,
  type PerspectiveCamera,
} from "three";

import { ActiveRallyVideo } from "./ActiveRallyVideo";
import type { FirefightClip } from "./GameStory_firefights";
import type {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import type { KitchenArrivalRow } from "./RallyBoardPopup";
import type { CvJson } from "../../shared/types/cvJson";
import type { InsightsJson } from "../../shared/types/insightsEtcTypes";

type ServingFilter = "all" | "team0" | "team1";
type PlayerId = 0 | 1 | 2 | 3;

type CameraView = {
  position: [number, number, number];
  target: [number, number, number];
};

type TrajectoryShot = {
  shotNumber: number;
  playerId: PlayerId | null;
  team: 0 | 1 | null;
  curve: QuadraticBezierCurve3;
  points: Vector3[];
  durationMs: number;
  startMs: number | null;
  endMs: number | null;
};

type CvBallPoint = {
  ms: number;
  point: Vector3;
};

type TrajectoryRally = {
  rallyIndex: number;
  servingTeam: 0 | 1 | null;
  winnerTeam: 0 | 1 | null;
  numShots: number;
  shots: TrajectoryShot[];
  cvBallPoints: CvBallPoint[];
};

const FALLBACK_SHOT_MS = 900;
const END_PAUSE_MS = 1100;
const INITIAL_VIEW: CameraView = {
  position: [0, 34, 42],
  target: [0, 0, 0],
};

function teamOfPlayer(playerId: number | null): 0 | 1 | null {
  if (playerId === 0 || playerId === 1) return 0;
  if (playerId === 2 || playerId === 3) return 1;
  return null;
}

function playerColor(playerId: number | null): string {
  switch (playerId) {
    case 0:
      return "#4da3ff";
    case 1:
      return "#9bcfff";
    case 2:
      return "#52d27c";
    case 3:
      return "#a7efbd";
    default:
      return "#e8e8e8";
  }
}

function asTeam01(value: unknown): 0 | 1 | null {
  return value === 0 || value === 1 ? value : null;
}

function pointFromTrajectoryLocation(location: any): Vector3 | null {
  if (!location || typeof location !== "object") return null;
  const x = Number(location.x);
  const y = Number(location.y);
  const z = Number(location.z ?? 0);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return null;
  }

  // PBV court coordinates appear to use x across the 20 ft width and y along
  // the 44 ft length. Three is Y-up, so PBV z becomes vertical Y here.
  return new Vector3(x - 10, Math.max(0, z), y - 22);
}

function curveFromShot(shot: any): QuadraticBezierCurve3 | null {
  const trajectory = shot?.resulting_ball_movement?.trajectory;
  const start = pointFromTrajectoryLocation(trajectory?.start?.location);
  const peak = pointFromTrajectoryLocation(trajectory?.peak);
  const end = pointFromTrajectoryLocation(trajectory?.end?.location);
  if (!start || !peak || !end) return null;
  return new QuadraticBezierCurve3(start, peak, end);
}

function durationFromShot(shot: any): number {
  const trajectory = shot?.resulting_ball_movement?.trajectory;
  const startMs = Number(trajectory?.start?.ms);
  const endMs = Number(trajectory?.end?.ms);
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
    return endMs - startMs;
  }
  return FALLBACK_SHOT_MS;
}

function shotTimeFromTrajectory(shot: any): { startMs: number | null; endMs: number | null } {
  const trajectory = shot?.resulting_ball_movement?.trajectory;
  const startMs = Number(trajectory?.start?.ms);
  const endMs = Number(trajectory?.end?.ms);
  return {
    startMs: Number.isFinite(startMs) ? startMs : null,
    endMs: Number.isFinite(endMs) ? endMs : null,
  };
}

function courtPointFromCvBall(ball: any): Vector3 | null {
  return pointFromTrajectoryLocation(ball?.court_position);
}

function extractCvBallPointsForRally(args: {
  cvJson: CvJson | null | undefined;
  rallyIndex: number;
}): CvBallPoint[] {
  const { cvJson, rallyIndex } = args;
  const rally = cvJson?.sessions?.[0]?.rallies?.[rallyIndex];
  const fps = Number(cvJson?.camera?.fps);
  if (!rally?.frames?.length || !Number.isFinite(fps) || fps <= 0) return [];

  const out: CvBallPoint[] = [];
  rally.frames.forEach((frame: any, frameOffset: number) => {
    const balls = frame?.balls;
    const selected = balls?.selected;
    const ball =
      (selected && balls?.[selected]?.court_position ? balls[selected] : null) ??
      balls?.ball ??
      balls?.shot ??
      balls?.bounce ??
      balls?.net ??
      null;
    const point = courtPointFromCvBall(ball);
    if (!point) return;
    out.push({
      ms: (frameOffset / fps) * 1000,
      point,
    });
  });

  return out;
}

function buildTrajectoryRallies(args: {
  rows: KitchenArrivalRow[];
  insightsJson: InsightsJson | null | undefined;
  cvJson: CvJson | null | undefined;
  playerIdToTeam?: (playerId: number | null) => 0 | 1 | null;
}): TrajectoryRally[] {
  const rowsByIndex = new Map(args.rows.map((row) => [row.rallyIndex, row]));
  const rallies = Array.isArray((args.insightsJson as any)?.rallies)
    ? ((args.insightsJson as any).rallies as any[])
    : [];

  return rallies
    .map((rally, arrayIndex): TrajectoryRally | null => {
      const explicitIndex =
        typeof rally?.rally_index === "number" ? rally.rally_index : arrayIndex;
      const row = rowsByIndex.get(explicitIndex);
      const shots = Array.isArray(rally?.shots) ? rally.shots : [];

      const trajectoryShots: TrajectoryShot[] = shots
        .map((shot: any, shotIndex: number): TrajectoryShot | null => {
          const curve = curveFromShot(shot);
          if (!curve) return null;
          const playerId =
            shot?.player_id === 0 ||
            shot?.player_id === 1 ||
            shot?.player_id === 2 ||
            shot?.player_id === 3
              ? (shot.player_id as PlayerId)
              : null;
          const shotTime = shotTimeFromTrajectory(shot);
          return {
            shotNumber: shotIndex + 1,
            playerId,
            team: args.playerIdToTeam?.(playerId) ?? teamOfPlayer(playerId),
            curve,
            points: curve.getPoints(12),
            durationMs: durationFromShot(shot),
            startMs: shotTime.startMs,
            endMs: shotTime.endMs,
          };
        })
        .filter((shot: TrajectoryShot | null): shot is TrajectoryShot => !!shot);

      if (!trajectoryShots.length) return null;

      return {
        rallyIndex: explicitIndex,
        servingTeam:
          asTeam01(row?.servingTeam) ??
          args.playerIdToTeam?.(
            shots[0]?.player_id === 0 ||
              shots[0]?.player_id === 1 ||
              shots[0]?.player_id === 2 ||
              shots[0]?.player_id === 3
              ? shots[0].player_id
              : null,
          ) ??
          null,
        winnerTeam: asTeam01(row?.winnerTeam ?? rally?.winning_team),
        numShots:
          typeof row?.numShots === "number" && Number.isFinite(row.numShots)
            ? row.numShots
            : shots.length,
        shots: trajectoryShots,
        cvBallPoints: extractCvBallPointsForRally({
          cvJson: args.cvJson,
          rallyIndex: explicitIndex,
        }),
      };
    })
    .filter((rally): rally is TrajectoryRally => !!rally);
}

function LinePoints(props: {
  points: Vector3[];
  color: string;
  opacity?: number;
  lineWidth?: number;
}) {
  const geometry = React.useMemo(
    () => new BufferGeometry().setFromPoints(props.points),
    [props.points],
  );

  React.useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <line>
      <primitive object={geometry} attach="geometry" />
      <lineBasicMaterial
        color={props.color}
        transparent
        opacity={props.opacity ?? 1}
        linewidth={props.lineWidth ?? 1}
      />
    </line>
  );
}

function CourtLines() {
  const outer = [
    new Vector3(-10, 0, -22),
    new Vector3(10, 0, -22),
    new Vector3(10, 0, 22),
    new Vector3(-10, 0, 22),
    new Vector3(-10, 0, -22),
  ];
  const lineColor = "#ffffff";
  return (
    <group>
      <LinePoints points={outer} color={lineColor} opacity={0.82} />
      <LinePoints
        points={[new Vector3(-10, 0, 0), new Vector3(10, 0, 0)]}
        color="#f0f0f0"
        opacity={0.95}
      />
      <LinePoints
        points={[new Vector3(-10, 0, -7), new Vector3(10, 0, -7)]}
        color={lineColor}
        opacity={0.66}
      />
      <LinePoints
        points={[new Vector3(-10, 0, 7), new Vector3(10, 0, 7)]}
        color={lineColor}
        opacity={0.66}
      />
      <LinePoints
        points={[new Vector3(0, 0, -22), new Vector3(0, 0, -7)]}
        color={lineColor}
        opacity={0.4}
      />
      <LinePoints
        points={[new Vector3(0, 0, 7), new Vector3(0, 0, 22)]}
        color={lineColor}
        opacity={0.4}
      />
    </group>
  );
}

function trajectoryOpacity(idx: number, currentShotIndex: number): number {
  if (idx === currentShotIndex) return 0.98;
  if (idx === currentShotIndex - 1) return 0.72;
  if (idx < currentShotIndex) return 0.44;
  return 0.32;
}

function interpolateCvBallPoint(points: CvBallPoint[], elapsedMs: number): Vector3 | null {
  if (!points.length) return null;
  if (elapsedMs <= points[0].ms) return points[0].point;
  const last = points[points.length - 1];
  if (elapsedMs >= last.ms) return last.point;

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const next = points[i];
    if (elapsedMs > next.ms) continue;
    const span = Math.max(1, next.ms - prev.ms);
    const t = (elapsedMs - prev.ms) / span;
    return prev.point.clone().lerp(next.point, t);
  }

  return last.point;
}

function shotNumberForElapsedMs(shots: TrajectoryShot[], elapsedMs: number): number {
  for (const shot of shots) {
    if (shot.startMs == null || shot.endMs == null) continue;
    if (elapsedMs >= shot.startMs && elapsedMs <= shot.endMs) {
      return shot.shotNumber;
    }
  }

  let cursor = 0;
  for (const shot of shots) {
    const duration = Math.max(1, shot.durationMs);
    if (elapsedMs < cursor + duration) return shot.shotNumber;
    cursor += duration;
  }

  return shots[shots.length - 1]?.shotNumber ?? 1;
}

function shotIndexForShotNumber(shots: TrajectoryShot[], shotNumber: number): number {
  const idx = shots.findIndex((shot) => shot.shotNumber === shotNumber);
  return idx >= 0 ? idx : 0;
}

function computeTrajectoryState(
  rally: TrajectoryRally,
  elapsedMs: number,
): { idx: number; point: Vector3 | null } {
  const cvMotionMs =
    rally.cvBallPoints.length >= 2
      ? rally.cvBallPoints[rally.cvBallPoints.length - 1].ms
      : 0;
  const insightMotionMs = rally.shots.reduce(
    (sum, shot) => sum + Math.max(1, shot.durationMs),
    0,
  );
  const shotMotionMs = Math.max(cvMotionMs, insightMotionMs);
  const elapsed = Math.max(0, Math.min(shotMotionMs + END_PAUSE_MS, elapsedMs));

  if (rally.cvBallPoints.length >= 2) {
    const cvElapsed = Math.min(elapsed, cvMotionMs);
    const shotNumber = shotNumberForElapsedMs(rally.shots, cvElapsed);
    return {
      idx: shotIndexForShotNumber(rally.shots, shotNumber),
      point: interpolateCvBallPoint(rally.cvBallPoints, cvElapsed),
    };
  }

  let idx = rally.shots.length - 1;
  let t = 1;
  if (elapsed < shotMotionMs) {
    let cursor = 0;
    for (let i = 0; i < rally.shots.length; i += 1) {
      const duration = Math.max(1, rally.shots[i].durationMs);
      if (elapsed < cursor + duration) {
        idx = i;
        t = (elapsed - cursor) / duration;
        break;
      }
      cursor += duration;
    }
  }

  return {
    idx,
    point: rally.shots[idx]?.curve.getPoint(t) ?? null,
  };
}

function MiniTrajectoryScene(props: {
  rally: TrajectoryRally;
  syncRotation: boolean;
  sharedView: CameraView;
  onSharedViewChange: (view: CameraView) => void;
  onCurrentShotNumberChange: (shotNumber: number) => void;
  syncElapsedMs?: number | null;
  pausedAtServe?: boolean;
}) {
  const ballRef = React.useRef<any>(null);
  const controlsRef = React.useRef<any>(null);
  const [currentShotIndex, setCurrentShotIndex] = React.useState(0);
  const currentShotIndexRef = React.useRef(0);
  const { camera, invalidate } = useThree();
  const servePoint = props.rally.shots[0]?.points[0] ?? null;
  const initialBallPoint = servePoint ?? props.rally.shots[0]?.curve.getPoint(0) ?? null;
  const serveColor = playerColor(props.rally.shots[0]?.playerId ?? null);

  React.useEffect(() => {
    if (!props.syncRotation) return;
    camera.position.set(...props.sharedView.position);
    controlsRef.current?.target.set(...props.sharedView.target);
    controlsRef.current?.update();
  }, [camera, props.sharedView, props.syncRotation]);

  const applyElapsedMs = React.useCallback(
    (elapsedMs: number) => {
      const { idx, point } = computeTrajectoryState(props.rally, elapsedMs);
      if (idx !== currentShotIndexRef.current) {
        currentShotIndexRef.current = idx;
        setCurrentShotIndex(idx);
        props.onCurrentShotNumberChange(props.rally.shots[idx].shotNumber);
      }
      if (point && ballRef.current) {
        ballRef.current.position.copy(point);
      }
      invalidate();
    },
    [invalidate, props],
  );

  React.useEffect(() => {
    if (props.pausedAtServe) {
      applyElapsedMs(0);
      return;
    }
    if (typeof props.syncElapsedMs === "number" && Number.isFinite(props.syncElapsedMs)) {
      applyElapsedMs(props.syncElapsedMs);
    }
  }, [applyElapsedMs, props.pausedAtServe, props.syncElapsedMs]);

  useFrame(({ clock }) => {
    if (props.pausedAtServe) return;
    if (typeof props.syncElapsedMs === "number" && Number.isFinite(props.syncElapsedMs)) {
      return;
    }

    const cvMotionMs =
      props.rally.cvBallPoints.length >= 2
        ? props.rally.cvBallPoints[props.rally.cvBallPoints.length - 1].ms
        : 0;
    const insightMotionMs = props.rally.shots.reduce(
      (sum, shot) => sum + Math.max(1, shot.durationMs),
      0,
    );
    const shotMotionMs = Math.max(cvMotionMs, insightMotionMs);
    const totalMs = shotMotionMs + END_PAUSE_MS;
    const elapsed = props.pausedAtServe
      ? 0
      : typeof props.syncElapsedMs === "number" && Number.isFinite(props.syncElapsedMs)
        ? Math.max(0, Math.min(totalMs, props.syncElapsedMs))
        : (clock.elapsedTime * 1000 + props.rally.rallyIndex * 113) % totalMs;
    applyElapsedMs(elapsed);
  });

  return (
    <>
      <ambientLight intensity={0.85} />
      <directionalLight position={[0, 28, -18]} intensity={0.6} />
      <CourtLines />
      {servePoint ? (
        <group position={[servePoint.x, servePoint.y, servePoint.z]}>
          <mesh position={[0, 0.08, 0]}>
            <cylinderGeometry args={[0.7, 0.7, 0.08, 24]} />
            <meshBasicMaterial color={serveColor} transparent opacity={0.82} />
          </mesh>
          <mesh position={[0, 0.52, 0]}>
            <sphereGeometry args={[0.22, 10, 10]} />
            <meshBasicMaterial color={serveColor} />
          </mesh>
        </group>
      ) : null}
      {props.rally.shots.map((shot, idx) => (
        <LinePoints
          key={`${props.rally.rallyIndex}-${shot.shotNumber}`}
          points={shot.points}
          color={playerColor(shot.playerId)}
          opacity={trajectoryOpacity(idx, currentShotIndex)}
          lineWidth={idx === currentShotIndex ? 2 : 1}
        />
      ))}
      {props.rally.cvBallPoints.length >= 2 ? (
        <LinePoints
          points={props.rally.cvBallPoints.map((p) => p.point)}
          color="#f5f04a"
          opacity={0.58}
          lineWidth={1}
        />
      ) : null}
      <mesh
        ref={ballRef}
        position={
          initialBallPoint
            ? [initialBallPoint.x, initialBallPoint.y, initialBallPoint.z]
            : [0, 0, 0]
        }
      >
        <sphereGeometry args={[0.45, 10, 10]} />
        <meshBasicMaterial color="#f5f04a" />
      </mesh>
      <OrbitControls
        ref={controlsRef}
        enablePan={false}
        enableZoom
        onEnd={() => {
          if (!props.syncRotation) return;
          const c = camera as PerspectiveCamera;
          const target = controlsRef.current?.target ?? new Vector3(0, 0, 0);
          props.onSharedViewChange({
            position: [c.position.x, c.position.y, c.position.z],
            target: [target.x, target.y, target.z],
          });
        }}
      />
    </>
  );
}

type MiniTrajectoryPlotProps = {
  rally: TrajectoryRally;
  selected: boolean;
  syncRotation: boolean;
  sharedView: CameraView;
  onSharedViewChange: (view: CameraView) => void;
  onSelect: (rallyIndex: number) => void;
  syncElapsedMs?: number | null;
  pausedAtServe?: boolean;
  onCanvasReady?: () => void;
};

function MiniTrajectoryPlot(props: MiniTrajectoryPlotProps) {
  const [currentShotNumber, setCurrentShotNumber] = React.useState(
    props.rally.shots[0]?.shotNumber ?? 1,
  );
  const [canvasReady, setCanvasReady] = React.useState(false);

  React.useEffect(() => {
    setCurrentShotNumber(props.rally.shots[0]?.shotNumber ?? 1);
    setCanvasReady(false);
  }, [props.rally]);

  return (
    <Box
      data-happy-path="trajectory-wall-plot"
      data-rally-index={props.rally.rallyIndex}
      data-trajectory-wall-canvas-ready={canvasReady ? "true" : "false"}
      onClick={() => props.onSelect(props.rally.rallyIndex)}
      sx={{
        height: 210,
        border: props.selected
          ? "2px solid rgba(255,215,90,0.95)"
          : "1px solid rgba(255,255,255,0.16)",
        borderRadius: 1,
        bgcolor: "rgba(255,255,255,0.035)",
        overflow: "hidden",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 0.75,
          py: 0.45,
          fontSize: 12,
          fontWeight: 800,
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <Box>
          Rally {props.rally.rallyIndex + 1} ({props.rally.numShots} shots) · Shot{" "}
          {currentShotNumber}
        </Box>
        <Box sx={{ color: "rgba(255,255,255,0.66)", fontWeight: 700 }}>
          Serving: {props.rally.servingTeam == null ? "?" : `Team ${props.rally.servingTeam}`}
          {" · "}
          Winner: {props.rally.winnerTeam == null ? "?" : `Team ${props.rally.winnerTeam}`}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Canvas
          frameloop={
            props.pausedAtServe ||
            (typeof props.syncElapsedMs === "number" && Number.isFinite(props.syncElapsedMs))
              ? "demand"
              : "always"
          }
          dpr={[0.6, 0.9]}
          gl={{ antialias: false, powerPreference: "low-power" }}
          camera={{ position: INITIAL_VIEW.position, fov: 42, near: 0.1, far: 200 }}
          onCreated={() => {
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => {
                setCanvasReady(true);
                props.onCanvasReady?.();
              });
            });
          }}
        >
          <color attach="background" args={["#080808"]} />
          <MiniTrajectoryScene
            rally={props.rally}
            syncRotation={props.syncRotation}
            sharedView={props.sharedView}
            onSharedViewChange={props.onSharedViewChange}
            onCurrentShotNumberChange={setCurrentShotNumber}
            syncElapsedMs={props.syncElapsedMs}
            pausedAtServe={props.pausedAtServe}
          />
        </Canvas>
      </Box>
    </Box>
  );
}

function LazyMiniTrajectoryPlot(props: MiniTrajectoryPlotProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [isNearViewport, setIsNearViewport] = React.useState(false);
  const [canvasReady, setCanvasReady] = React.useState(false);

  React.useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) {
      setIsNearViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setIsNearViewport(Boolean(entries[0]?.isIntersecting));
      },
      { root: null, rootMargin: "24px 0px", threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    setCanvasReady(false);
  }, [isNearViewport, props.rally]);

  return (
    <Box
      ref={rootRef}
      data-happy-path="trajectory-wall-tile"
      data-rally-index={props.rally.rallyIndex}
      data-trajectory-wall-active={isNearViewport ? "true" : "false"}
      data-trajectory-wall-canvas-ready={canvasReady ? "true" : "false"}
      sx={{ height: 210, minWidth: 0 }}
    >
      {isNearViewport ? (
        <MiniTrajectoryPlot
          {...props}
          onCanvasReady={() => {
            setCanvasReady(true);
            props.onCanvasReady?.();
          }}
        />
      ) : (
        <Box
          onClick={() => props.onSelect(props.rally.rallyIndex)}
          sx={{
            height: 210,
            border: props.selected
              ? "2px solid rgba(255,215,90,0.95)"
              : "1px solid rgba(255,255,255,0.16)",
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.035)",
            color: "rgba(255,255,255,0.54)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          Preparing trajectory...
        </Box>
      )}
    </Box>
  );
}

export function RallyTrajectoryWallPopup(props: {
  open: boolean;
  onClose: () => void;
  packageId: string;
  rows: KitchenArrivalRow[];
  insightsJson: InsightsJson | null | undefined;
  cvJson?: CvJson | null;
  renderPlayerIdentity?: RenderPlayerIdentity;
  playerIdToTeam?: (playerId: number | null) => 0 | 1 | null;
  insightsToCvPlayerIndex: PlayerIndexMap;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  onSelectRally?: (rallyIndex: number) => void;
}) {
  const {
    open,
    onClose,
    packageId,
    rows,
    insightsJson,
    cvJson,
    renderPlayerIdentity,
    playerIdToTeam,
    insightsToCvPlayerIndex,
    firefightsClips,
    firefightRallySet,
    onSelectRally,
  } = props;
  const [servingFilter, setServingFilter] = React.useState<ServingFilter>("all");
  const [syncRotation, setSyncRotation] = React.useState(true);
  const [sharedView, setSharedView] = React.useState<CameraView>(INITIAL_VIEW);
  const [selectedRallyIndex, setSelectedRallyIndex] = React.useState<number | null>(null);
  const [selectedVideoTimeSec, setSelectedVideoTimeSec] = React.useState(0);
  const selectedVideoRef = React.useRef<React.RefObject<HTMLVideoElement | null> | null>(null);
  const [frame, setFrame] = React.useState({
    left: 32,
    top: 32,
    width: 1780,
    height: 760,
  });
  const dragRef = React.useRef<null | {
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
  }>(null);
  const resizeRef = React.useRef<null | {
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  }>(null);

  React.useEffect(() => {
    function onMove(e: MouseEvent) {
      if (dragRef.current) {
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        setFrame((cur) => ({
          ...cur,
          left: Math.max(0, dragRef.current!.startLeft + dx),
          top: Math.max(0, dragRef.current!.startTop + dy),
        }));
      }
      if (resizeRef.current) {
        const dx = e.clientX - resizeRef.current.startX;
        const dy = e.clientY - resizeRef.current.startY;
        setFrame((cur) => ({
          ...cur,
          width: Math.max(1720, resizeRef.current!.startWidth + dx),
          height: Math.max(460, resizeRef.current!.startHeight + dy),
        }));
      }
    }
    function onUp() {
      dragRef.current = null;
      resizeRef.current = null;
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  React.useEffect(() => {
    if (!open) return;
    let lastTime = -1;
    const tick = () => {
      const video = selectedVideoRef.current?.current;
      const t = video?.currentTime;
      if (
        typeof t === "number" &&
        Number.isFinite(t) &&
        Math.abs(t - lastTime) >= 0.05
      ) {
        lastTime = t;
        setSelectedVideoTimeSec(t);
      }
    };
    const interval = window.setInterval(tick, 90);
    tick();
    return () => window.clearInterval(interval);
  }, [open, selectedRallyIndex]);

  const rallies = React.useMemo(
    () => buildTrajectoryRallies({ rows, insightsJson, cvJson, playerIdToTeam }),
    [rows, insightsJson, cvJson, playerIdToTeam],
  );

  const visibleRallies = React.useMemo(() => {
    if (servingFilter === "team0") return rallies.filter((r) => r.servingTeam === 0);
    if (servingFilter === "team1") return rallies.filter((r) => r.servingTeam === 1);
    return rallies;
  }, [rallies, servingFilter]);

  const selectRally = React.useCallback(
    (rallyIndex: number) => {
      setSelectedRallyIndex(rallyIndex);
      setSelectedVideoTimeSec(0);
      onSelectRally?.(rallyIndex);
    },
    [onSelectRally],
  );

  const selectedRally =
    visibleRallies.find((rally) => rally.rallyIndex === selectedRallyIndex) ??
    visibleRallies[0] ??
    null;
  const selectedRow = selectedRally
    ? rows.find((row) => row.rallyIndex === selectedRally.rallyIndex) ?? null
    : null;
  const selectedDurationSec =
    selectedRow && Number.isFinite(selectedRow.endMs - selectedRow.startMs)
      ? Math.max(0.5, (selectedRow.endMs - selectedRow.startMs) / 1000)
      : 2;

  if (!open) return null;

  return (
    <Box
      data-happy-path="trajectory-wall-popup"
      sx={{
        position: "fixed",
        left: frame.left,
        top: frame.top,
        width: frame.width,
        height: frame.height,
        zIndex: 4600,
        bgcolor: "rgba(12,12,12,0.98)",
        color: "rgba(255,255,255,0.92)",
        border: "1px solid rgba(255,255,255,0.20)",
        borderRadius: 1,
        boxShadow: "0 18px 70px rgba(0,0,0,0.48)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        onMouseDown={(e) => {
          dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startLeft: frame.left,
            startTop: frame.top,
          };
        }}
        sx={{
          px: 1.35,
          py: 0.85,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          cursor: "move",
          userSelect: "none",
        }}
      >
        <Box sx={{ fontSize: 18, fontWeight: 900 }}>Trajectory Wall</Box>
        <Box sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.62)" }}>
          Prototype: all rallies, CV ball track when available plus PBV start/peak/end curves.
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton
          size="small"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          sx={{ color: "rgba(255,255,255,0.72)" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 1.35,
          py: 0.7,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          bgcolor: "rgba(255,255,255,0.035)",
          fontSize: 12.5,
          lineHeight: 1.35,
          color: "rgba(255,255,255,0.74)",
        }}
      >
        <Box sx={{ fontWeight: 900, color: "rgba(255,255,255,0.9)", mb: 0.25 }}>
          Experiment note
        </Box>
        I wondered if anything insightful could be obtained by showing a 3D
        animation of all of the rallies. Even if it wasn't slow, it doesn't
        initially look like it will be very useful, imo.
      </Box>

      <Box
        sx={{
          px: 1,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ fontSize: 12, fontWeight: 900 }}>Serving</Box>
        {[
          ["all", "All rallies"],
          ["team0", "Team 0 serving"],
          ["team1", "Team 1 serving"],
        ].map(([key, label]) => (
          <Button
            key={key}
            size="small"
            variant={servingFilter === key ? "contained" : "outlined"}
            onClick={() => setServingFilter(key as ServingFilter)}
            sx={{ textTransform: "none", minHeight: 28, py: 0.1 }}
          >
            {label}
          </Button>
        ))}
        <Button
          size="small"
          variant={syncRotation ? "contained" : "outlined"}
          onClick={() => setSyncRotation((v) => !v)}
          sx={{ textTransform: "none", minHeight: 28, py: 0.1, ml: 1 }}
        >
          Sync rotation {syncRotation ? "ON" : "OFF"}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: "flex", gap: 1.2, fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
          <Box sx={{ color: playerColor(0) }}>Team 0 Player A</Box>
          <Box sx={{ color: playerColor(1) }}>Team 0 Player B</Box>
          <Box sx={{ color: playerColor(2) }}>Team 1 Player A</Box>
          <Box sx={{ color: playerColor(3) }}>Team 1 Player B</Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          gap: 1,
          p: 1,
        }}
      >
        <Box
          data-happy-path="trajectory-wall-grid"
          sx={{
            flex: "0 0 1180px",
            minWidth: 0,
            overflowY: "auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 1,
            pr: 0.5,
          }}
        >
          {visibleRallies.map((rally) => (
            <LazyMiniTrajectoryPlot
              key={rally.rallyIndex}
              rally={rally}
              selected={selectedRally?.rallyIndex === rally.rallyIndex}
              syncRotation={syncRotation}
              sharedView={sharedView}
              onSharedViewChange={setSharedView}
              onSelect={selectRally}
              pausedAtServe={true}
            />
        ))}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 360,
            borderLeft: "1px solid rgba(255,255,255,0.12)",
            pl: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {selectedRally ? (
            <Box sx={{ height: 230, mb: 1, flex: "0 0 auto" }}>
              <MiniTrajectoryPlot
                rally={selectedRally}
                selected={true}
                syncRotation={syncRotation}
                sharedView={sharedView}
                onSharedViewChange={setSharedView}
                onSelect={selectRally}
                syncElapsedMs={selectedVideoTimeSec * 1000}
                pausedAtServe={false}
              />
            </Box>
          ) : null}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {selectedRow ? (
              <ActiveRallyVideo
                aiOpenViaParent={false}
                packageId={packageId}
                row={selectedRow}
                rows={rows}
                activeShotNumber={null}
                focusedFilter={null}
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                renderPlayerIdentity={renderPlayerIdentity}
                aiContextLabel="Trajectory Wall"
                showCondensedTimeline={false}
                fontColor="#ffffff"
                controlsPlacement="left"
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls={true}
                showTitle={false}
                showScoreEtc={false}
                autoPlayOnRallyChange={true}
                showAi={true}
                showLoopScrubber={true}
                defaultLoopUiOpen={false}
                showPopoutButton={true}
                isInFullPopup={false}
                cvJson={cvJson}
                seedPlayback={{
                  timeSec: 0,
                  paused: false,
                  nonce: selectedRow.rallyIndex,
                }}
                seedLoopRegion={{
                  enabled: false,
                  aSec: 0,
                  bSec: selectedDurationSec,
                  nonce: selectedRow.rallyIndex,
                }}
                onVideoRef={(ref) => {
                  selectedVideoRef.current = ref;
                }}
              />
            ) : (
              <Box sx={{ color: "rgba(255,255,255,0.62)", fontSize: 13 }}>
                Pick a rally from the wall.
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        onMouseDown={(e) => {
          e.preventDefault();
          resizeRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startWidth: frame.width,
            startHeight: frame.height,
          };
        }}
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: 18,
          height: 18,
          cursor: "nwse-resize",
          borderRight: "2px solid rgba(255,255,255,0.45)",
          borderBottom: "2px solid rgba(255,255,255,0.45)",
        }}
      />
    </Box>
  );
}
